import React, { useState, useEffect } from 'react';
import { Organization, User, UserRole } from '../types';
import { getOrganizations, updateOrganizationStatus, createOrganizationWithUser, updateOrganization, deleteOrganization } from '../services/apiService';
import { LoadingSpinner } from './icons';
import OrganizationModal from './OrganizationModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';

// This is a bit of a workaround to get the current user for logging,
// since we don't have a global state management system like Redux or Context.
// In a real app, this would be handled more elegantly.
const getCurrentUserFromSession = (): User | null => {
    const storedUser = sessionStorage.getItem('currentUser');
    return storedUser ? JSON.parse(storedUser) : null;
}

const OrganizationCard: React.FC<{
    org: Organization;
    onEdit: () => void;
    onStatusChange: (newStatus: boolean) => void;
    onDelete: () => void;
}> = ({ org, onEdit, onStatusChange, onDelete }) => (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-start">
            <div>
                <p className="font-bold text-gray-900">{org.name}</p>
                <p className="text-sm text-gray-500">{org.contactPerson} ({org.email})</p>
            </div>
             <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${org.isActive ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {org.isActive ? 'Active' : 'Pending'}
            </span>
        </div>
        <div className="mt-4 pt-4 border-t flex justify-end items-center gap-2 text-sm font-medium">
             <button onClick={onEdit} className="text-primary-light hover:text-primary-dark">Edit</button>
             {org.isActive ? (
                <button onClick={() => onStatusChange(false)} className="text-yellow-600 hover:text-yellow-900">Deactivate</button>
             ) : (
                <button onClick={() => onStatusChange(true)} className="text-green-600 hover:text-green-900">Activate</button>
             )}
              <button onClick={onDelete} className="text-red-600 hover:text-red-900">Delete</button>
        </div>
    </div>
);


const OrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  
  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch (err) {
      setError('Failed to fetch organizations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const openModalForEdit = (org: Organization) => {
    setSelectedOrg(org);
    setIsModalOpen(true);
  };
  
  const openModalForAdd = () => {
    setSelectedOrg(null);
    setIsModalOpen(true);
  };

  const handleSave = async (data: any, orgId: string | null) => {
    const currentUser = getCurrentUserFromSession();
    if (!currentUser) {
        alert("Session expired. Please log in again.");
        return;
    }

    if (orgId) { // Editing
      await updateOrganization(orgId, data, currentUser);
    } else { // Creating
      await createOrganizationWithUser(data.orgData, data.userData, currentUser);
    }
    await fetchOrganizations(); // Refresh list
  };
  
  const handleStatusChange = async (orgId: string, newStatus: boolean) => {
    const currentUser = getCurrentUserFromSession();
     if (!currentUser) {
        alert("Session expired. Please log in again.");
        return;
    }
    const originalOrganizations = [...organizations];
    try {
      // Optimistic update
      setOrganizations(prev => prev.map(org => org.id === orgId ? {...org, isActive: newStatus} : org));
      await updateOrganizationStatus(orgId, newStatus, currentUser);
    } catch (err) {
      alert("Failed to update status. Reverting changes.");
      setOrganizations(originalOrganizations);
    }
  };

   const handleConfirmDelete = async () => {
    const currentUser = getCurrentUserFromSession();
    if (!currentUser || !orgToDelete) {
        alert("Session expired or organization not selected. Please try again.");
        return;
    }
    if (currentUser.role !== UserRole.SUPER_ADMIN) {
        alert("You do not have permission to delete organizations.");
        return;
    }

    try {
        await deleteOrganization(orgToDelete.id, currentUser);
        setOrgToDelete(null); // Close modal on success
        await fetchOrganizations(); // Refresh list
    } catch (err) {
        alert(`Failed to delete organization: ${(err as Error).message}`);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Manage Taxi Networks</h1>
        <button
            onClick={openModalForAdd}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light w-full sm:w-auto"
        >
            Add New Network
        </button>
      </div>

      {loading && <div className="flex justify-center items-center py-8"><LoadingSpinner className="w-8 h-8 text-primary" /></div>}
      {error && <div className="text-red-500 p-4 bg-red-100 rounded-md">{error}</div>}

      {!loading && !error && (
        <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Network Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {organizations.map(org => (
                        <tr key={org.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{org.name}</div>
                            <div className="text-sm text-gray-500">{org.abn}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{org.contactPerson}</div>
                            <div className="text-sm text-gray-500">{org.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${org.isActive ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {org.isActive ? 'Active' : 'Pending Approval'}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                            <button onClick={() => openModalForEdit(org)} className="text-primary-light hover:text-primary-dark">Edit</button>
                            {org.isActive ? (
                                <button onClick={() => handleStatusChange(org.id, false)} className="text-yellow-600 hover:text-yellow-900">Deactivate</button>
                            ) : (
                                <button onClick={() => handleStatusChange(org.id, true)} className="text-green-600 hover:text-green-900">Activate</button>
                            )}
                             <button onClick={() => setOrgToDelete(org)} className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {organizations.map(org => (
                    <OrganizationCard
                        key={org.id}
                        org={org}
                        onEdit={() => openModalForEdit(org)}
                        onStatusChange={(newStatus) => handleStatusChange(org.id, newStatus)}
                        onDelete={() => setOrgToDelete(org)}
                    />
                ))}
            </div>
        </>
      )}

      {isModalOpen && (
        <OrganizationModal
            organization={selectedOrg}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
        />
      )}
      {orgToDelete && (
        <ConfirmDeleteModal
            isOpen={!!orgToDelete}
            onClose={() => setOrgToDelete(null)}
            onConfirm={handleConfirmDelete}
            resourceName={orgToDelete.name}
            confirmText="DELETE"
        />
      )}
    </div>
  );
};

export default OrganizationsPage;